import { useState, useRef, useEffect, useCallback } from 'react';
import { fetchByISBN, searchByTitle } from '../utils/firestoreService';

const F = "'Inter',-apple-system,sans-serif";
const T = {
  orange:'var(--orange)', ink:'var(--ink)', surface:'var(--surface)',
  border:'var(--border)', muted:'var(--muted)', subtle:'var(--subtle)',
  g1:'var(--surface2)', green:'#16A34A', red:'#DC2626',
};

// ── Détection ISBN sans ZXing : canvas + BarcodeDetector API (Chrome) ─────────
// Sur iOS Safari : BarcodeDetector n'existe pas → on dessine les frames
// sur un canvas et on utilise une lib WASM minimale chargée lazy

let quaggaPromise = null;
const loadQuagga = () => {
  if (quaggaPromise) return quaggaPromise;
  quaggaPromise = new Promise((resolve, reject) => {
    if (window.Quagga) { resolve(window.Quagga); return; }
    const s = document.createElement('script');
    // QuaggaJS v0.12 — lib légère de scan code-barres, marche sur iOS Safari
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/quagga/0.12.1/quagga.min.js';
    s.onload = () => resolve(window.Quagga);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return quaggaPromise;
};

// ── Reconnaissance par photo de couverture : OCR client-side (Tesseract.js) ──
let tesseractPromise = null;
const loadTesseract = () => {
  if (tesseractPromise) return tesseractPromise;
  tesseractPromise = new Promise((resolve, reject) => {
    if (window.Tesseract) { resolve(window.Tesseract); return; }
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@5.1.1/dist/tesseract.min.js';
    s.onload = () => resolve(window.Tesseract);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return tesseractPromise;
};

// Heuristique : le titre d'une couverture est en général la ligne de texte
// reconnue la plus "grande" (hauteur de boîte) parmi celles suffisamment fiables.
const extractLikelyTitle = (data) => {
  const lines = (data?.lines || [])
    .map(l => ({
      text: (l.text || '').trim(),
      height: (l.bbox?.y1 || 0) - (l.bbox?.y0 || 0),
      confidence: l.confidence || 0,
    }))
    .filter(l => l.text.length >= 3 && l.confidence >= 35 && !/^\d+$/.test(l.text));
  if (!lines.length) return (data?.text || '').split('\n').map(s=>s.trim()).filter(Boolean)[0] || '';
  lines.sort((a,b) => b.height - a.height);
  // Prend la plus grande ligne, et une 2e si elle est quasi aussi grande (titre sur 2 lignes)
  const top = [lines[0]];
  if (lines[1] && lines[1].height >= lines[0].height * 0.7) top.push(lines[1]);
  return top.map(l=>l.text).join(' ').slice(0, 80);
};

export default function ISBNScanner({ onFound, onClose }) {
  const videoRef     = useRef(null);
  const canvasRef    = useRef(null);
  const streamRef    = useRef(null);
  const rafRef       = useRef(null);
  const quaggaRef    = useRef(null);
  const mountedRef   = useRef(true);

  const [mode, setMode]         = useState('choice');
  const [isbn, setIsbn]         = useState('');
  const [loading, setLoading]   = useState(false);
  const [starting, setStart]    = useState(false);
  const [scanning, setScanning] = useState(false);
  const [camErr, setCamErr]     = useState('');
  const [detected, setDetected] = useState('');
  const [error, setError]       = useState('');
  const [libLoading, setLibLoad] = useState(false);

  // ── Reconnaissance par photo de couverture ──
  const photoInputRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [ocrLoading, setOcrLoading]     = useState(false);
  const [ocrProgress, setOcrProgress]   = useState(0);
  const [photoQuery, setPhotoQuery]     = useState('');
  const [photoResults, setPhotoResults] = useState(null); // null = pas encore cherché
  const [photoSearching, setPhotoSearch]= useState(false);
  const [photoError, setPhotoError]     = useState('');

  const stopAll = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (quaggaRef.current) {
      try { quaggaRef.current.stop(); } catch {}
      quaggaRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; stopAll(); };
  }, [stopAll]);

  const handleISBNCode = useCallback(async (code) => {
    const clean = code.replace(/[\s-]/g, '');

    // Tout EAN-13 commençant par 978 ou 979 est un ISBN livre
    // Les autres EAN-13 (codes-barres produit) peuvent quand même être utilisés
    const isISBN = /^(978|979)\d{10}$/.test(clean);
    const isEAN  = /^\d{13}$/.test(clean);
    const isISBN10 = /^\d{10}$/.test(clean);

    if (!isISBN && !isEAN && !isISBN10) {
      // Code non reconnu du tout — afficher quand même en manuel
      setMode('manual');
      setIsbn(clean);
      setError(`Code "${clean}" scanné. Vérifie qu'il s'agit bien d'un ISBN.`);
      return;
    }

    setLoading(true); setError('');

    // Toujours chercher dans les APIs
    const result = await fetchByISBN(clean);
    setLoading(false);

    if (result) {
      // API trouvé → pré-remplir le formulaire avec toutes les infos + l'ISBN
      onFound({ ...result, isbn: clean });
    } else {
      // API vide → pré-remplir quand même l'ISBN dans le formulaire
      // L'utilisateur complètera les autres champs manuellement
      onFound({ isbn: clean });
    }
  }, [onFound]);

  // ── Approche 1 : BarcodeDetector natif (Chrome Android, Chrome Desktop) ────
  const startWithBarcodeDetector = useCallback(async (stream) => {
    const bd = new window.BarcodeDetector({ formats: ['ean_13','ean_8','upc_a','upc_e','code_128'] });
    const video = videoRef.current;

    const scan = async () => {
      if (!mountedRef.current || !streamRef.current) return;
      try {
        const codes = await bd.detect(video);
        if (codes.length > 0) {
          const code = codes[0].rawValue;
          stopAll();
          if (mountedRef.current) { setScanning(false); setDetected(code); handleISBNCode(code); }
          return;
        }
      } catch {}
      rafRef.current = requestAnimationFrame(scan);
    };
    rafRef.current = requestAnimationFrame(scan);
  }, [stopAll, handleISBNCode]);

  // ── Approche 2 : QuaggaJS (iOS Safari, Firefox) ───────────────────────────
  const startWithQuagga = useCallback(async (stream) => {
    setLibLoad(true);
    try {
      const Quagga = await loadQuagga();
      quaggaRef.current = Quagga;
      setLibLoad(false);
      if (!mountedRef.current) return;

      const video = videoRef.current;

      await new Promise((resolve, reject) => {
        Quagga.init({
          inputStream: {
            type: 'LiveStream',
            target: video,
            constraints: {
              facingMode: 'environment',
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          },
          decoder: {
            readers: ['ean_reader', 'ean_8_reader', 'upc_reader', 'upc_e_reader', 'code_128_reader'],
          },
          locate: true,
          numOfWorkers: 0, // pas de Web Workers → compatible iOS
        }, (err) => {
          if (err) { reject(err); return; }
          resolve();
        });
      });

      Quagga.start();

      Quagga.onDetected((result) => {
        const code = result.codeResult?.code;
        if (!code || !mountedRef.current) return;
        stopAll();
        setScanning(false); setDetected(code); handleISBNCode(code);
      });

    } catch(e) {
      setLibLoad(false);
      throw e;
    }
  }, [stopAll, handleISBNCode]);

  // ── Démarrage caméra ───────────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    setMode('camera'); setCamErr(''); setError(''); setDetected(''); setStart(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width:  { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;

      // IMPORTANT iOS : attacher srcObject avant tout, vidéo visible dans le DOM
      const video = videoRef.current;
      if (!video) { stopAll(); return; }

      // Attributs obligatoires pour iOS Safari
      video.playsInline = true;
      video.muted = true;
      video.autoplay = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.srcObject = stream;

      // Attendre que la vidéo joue vraiment
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Timeout démarrage vidéo')), 10000);
        const onPlay = () => { clearTimeout(timeout); video.removeEventListener('playing', onPlay); resolve(); };
        video.addEventListener('playing', onPlay);
        video.play().catch(reject);
      });

      if (!mountedRef.current) { stopAll(); return; }
      setStart(false); setScanning(true);

      // Choisir la méthode de décodage selon le navigateur
      if ('BarcodeDetector' in window) {
        await startWithBarcodeDetector(stream);
      } else {
        await startWithQuagga(stream);
      }

    } catch(e) {
      if (!mountedRef.current) return;
      setStart(false); setScanning(false); stopAll();
      const msg = e?.message || String(e);
      if (/NotAllowed|Permission|denied/i.test(msg) || e?.name === 'NotAllowedError') {
        setCamErr('Accès à la caméra refusé.\n\n📱 iPhone : Réglages → Safari → Caméra → Autoriser\n🤖 Android : Appui long navigateur → Autorisations → Caméra\n\nOu utilise la saisie manuelle ci-dessous.');
      } else if (e?.name === 'NotFoundError') {
        setCamErr('Aucune caméra trouvée.');
      } else if (e?.name === 'NotReadableError') {
        setCamErr('Caméra occupée par une autre app. Ferme-la et réessaie.');
      } else if (/timeout/i.test(msg)) {
        setCamErr('La caméra a mis trop de temps à démarrer.');
      } else {
        setCamErr(`Erreur : ${msg}`);
      }
    }
  }, [startWithBarcodeDetector, startWithQuagga, stopAll]);

  const submitManual = async (e) => { e.preventDefault(); stopAll(); await handleISBNCode(isbn); };
  const goManual = () => { stopAll(); setScanning(false); setMode('manual'); setCamErr(''); };
  const goBack   = () => { stopAll(); setScanning(false); setMode('choice'); setCamErr(''); setError(''); };

  const goPhoto = () => {
    setMode('photo'); setPhotoPreview(null); setPhotoQuery(''); setPhotoResults(null); setPhotoError('');
  };

  const handlePhotoFile = async (file) => {
    if (!file) return;
    setPhotoResults(null); setPhotoError(''); setPhotoQuery('');
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);

    setOcrLoading(true); setOcrProgress(0);
    try {
      const Tesseract = await loadTesseract();
      const { data } = await Tesseract.recognize(file, 'fra', {
        logger: m => { if (m.status === 'recognizing text') setOcrProgress(Math.round((m.progress||0)*100)); },
      });
      const guess = extractLikelyTitle(data);
      setPhotoQuery(guess);
    } catch (e) {
      setPhotoError("Lecture du texte impossible — vérifie ta connexion, ou saisis le titre à la main ci-dessous.");
    }
    setOcrLoading(false);
  };

  const handlePhotoSearch = async () => {
    if (!photoQuery.trim()) return;
    setPhotoSearch(true); setPhotoError(''); setPhotoResults(null);
    try {
      const results = await searchByTitle(photoQuery);
      setPhotoResults(results);
      if (!results.length) setPhotoError("Aucun résultat pour ce titre. Essaie de corriger le texte, ou saisis l'ISBN à la main.");
    } catch {
      setPhotoError('Recherche impossible pour le moment.');
    }
    setPhotoSearch(false);
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(28,22,18,.75)',backdropFilter:'blur(8px)',WebkitBackdropFilter:'blur(8px)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:1100}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:T.surface,borderRadius:'22px 22px 0 0',border:`1px solid ${T.border}`,borderBottom:'none',width:'100%',maxWidth:'520px',boxShadow:'0 -12px 40px rgba(0,0,0,.2)',animation:'slideUp .3s cubic-bezier(.22,1,.36,1)',fontFamily:F}}>

        {/* Header */}
        <div style={{padding:'1rem 1.25rem',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontSize:'16px',fontWeight:700,color:T.ink}}>Scanner un ISBN</div>
            <div style={{fontSize:'12px',color:T.muted,marginTop:'1px'}}>
              {mode==='camera' ? 'Place le code-barres dans le cadre orange' : mode==='photo' ? 'Prends la couverture en photo' : 'Code-barres au dos du livre'}
            </div>
          </div>
          <button onClick={onClose} style={{width:'32px',height:'32px',borderRadius:'50%',border:`1px solid ${T.border}`,background:T.g1,cursor:'pointer',fontSize:'18px',color:T.muted,display:'flex',alignItems:'center',justifyContent:'center',padding:0}}>×</button>
        </div>

        <div style={{padding:'1.25rem'}}>

          {/* ── Choix ── */}
          {mode==='choice' && (
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              <ChoiceBtn icon="📷" title="Scanner avec la caméra" sub="Compatible iPhone, Android, desktop" onClick={startCamera}/>
              <ChoiceBtn icon="📸" title="Photo de la couverture" sub="Pour les éditions sans ISBN lisible" onClick={goPhoto}/>
              <ChoiceBtn icon="⌨️" title="Saisir l'ISBN manuellement" sub="Les 13 chiffres du code-barres" onClick={()=>setMode('manual')}/>
              <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:'10px',padding:'10px 12px',fontSize:'12px',color:'#92400E',lineHeight:1.6}}>
                💡 <strong>iPhone :</strong> la 1ère fois, Safari demande l'accès à la caméra — accepte. Si tu as déjà refusé : Réglages → Safari → Caméra → Autoriser.
              </div>
            </div>
          )}

          {/* ── Caméra ── */}
          {mode==='camera' && (
            <div>
              {/* Le <video> est TOUJOURS dans le DOM dès qu'on est en mode camera */}
              {/* iOS Safari exige que l'élément soit visible avant play() */}
              <div style={{
                position:'relative', borderRadius:'14px', overflow:'hidden',
                background:'#000', aspectRatio:'4/3', marginBottom:'14px',
                // Caché visuellement pendant le chargement, mais présent dans le DOM
                opacity: starting ? 0 : 1,
                transition: 'opacity .3s',
                // Ne jamais faire display:none — ça casse iOS
              }}>
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  autoPlay
                  // webkit-playsinline est crucial pour les vieux iPhones
                  style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}
                />
                <canvas ref={canvasRef} style={{display:'none'}}/>

                {/* Viseur */}
                {!starting && !camErr && (
                  <>
                    <svg style={{position:'absolute',inset:0,width:'100%',height:'100%',pointerEvents:'none'}} viewBox="0 0 100 100" preserveAspectRatio="none">
                      <path fillRule="evenodd" fill="rgba(0,0,0,.38)" d="M0 0h100v100H0z M8 35h84v30H8z"/>
                      <rect x="8" y="35" width="84" height="30" fill="none" stroke="rgba(240,78,26,.85)" strokeWidth="0.6" rx="1"/>
                      <path d="M8 43 L8 35 L16 35M84 35 L92 35 L92 43M8 57 L8 65 L16 65M84 65 L92 65 L92 57"
                        fill="none" stroke="var(--orange)" strokeWidth="2.2" strokeLinecap="round"/>
                    </svg>
                    {scanning && (
                      <div style={{position:'absolute',left:'8%',right:'8%',height:'2px',background:'linear-gradient(90deg,transparent,rgba(240,78,26,.95) 30%,rgba(240,78,26,.95) 70%,transparent)',animation:'scanline 2s ease-in-out infinite',top:'50%'}}/>
                    )}
                    <div style={{position:'absolute',bottom:'10px',left:0,right:0,display:'flex',justifyContent:'center'}}>
                      <div style={{background:'rgba(0,0,0,.65)',backdropFilter:'blur(4px)',color:'rgba(255,255,255,.92)',fontSize:'12px',fontWeight:600,padding:'5px 14px',borderRadius:'99px',fontFamily:F}}>
                        {libLoading ? '⏳ Chargement du scanner…' : scanning ? '🔍 Recherche du code-barres…' : '⏳ Initialisation…'}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Spinner de démarrage (par-dessus, pas display:none sur la vidéo) */}
              {starting && (
                <div style={{position:'absolute',left:0,right:0,display:'flex',flexDirection:'column',alignItems:'center',gap:'10px',padding:'1.5rem',color:T.muted}}>
                  <Spinner size={32}/>
                  <div style={{fontSize:'13px',fontWeight:600}}>Démarrage de la caméra…</div>
                  <div style={{fontSize:'11px',color:T.subtle}}>Autorise l'accès si Safari le demande</div>
                </div>
              )}

              {/* Erreur caméra */}
              {camErr && (
                <div style={{marginBottom:'12px'}}>
                  <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:'10px',padding:'14px',fontSize:'13px',color:T.red,marginBottom:'10px',lineHeight:1.7,whiteSpace:'pre-line'}}>
                    ⚠️ {camErr}
                  </div>
                  <PrimaryBtn onClick={()=>setMode('manual')} label="Saisir l'ISBN manuellement →"/>
                </div>
              )}

              {!starting && !camErr && (
                <SecondaryBtn onClick={goManual} label="↩ Saisir l'ISBN manuellement"/>
              )}
            </div>
          )}

          {/* ── Photo de couverture ── */}
          {mode==='photo' && (
            <div>
              <input ref={photoInputRef} type="file" accept="image/*" capture="environment"
                onChange={e=>handlePhotoFile(e.target.files?.[0])} style={{display:'none'}}/>

              {!photoPreview && (
                <button type="button" onClick={()=>photoInputRef.current?.click()}
                  style={{width:'100%',padding:'2.5rem 1rem',border:`2px dashed ${T.border}`,borderRadius:'14px',background:T.g1,cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',gap:'10px',fontFamily:F}}>
                  <span style={{fontSize:'36px'}}>📸</span>
                  <span style={{fontSize:'14px',fontWeight:700,color:T.ink}}>Prendre la couverture en photo</span>
                  <span style={{fontSize:'12px',color:T.muted}}>Cadre bien le titre, en évitant les reflets</span>
                </button>
              )}

              {photoPreview && (
                <div style={{display:'flex',gap:'12px',marginBottom:'14px'}}>
                  <img src={photoPreview} alt="Couverture" style={{width:'90px',height:'120px',objectFit:'cover',borderRadius:'8px',border:`1px solid ${T.border}`,flexShrink:0}}/>
                  <div style={{flex:1,minWidth:0}}>
                    {ocrLoading ? (
                      <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                          <Spinner size={16}/>
                          <span style={{fontSize:'12.5px',fontWeight:600,color:T.muted}}>Lecture du texte… {ocrProgress>0?`${ocrProgress}%`:''}</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <label style={{display:'block',fontSize:'11px',fontWeight:600,color:T.muted,marginBottom:'5px',textTransform:'uppercase',letterSpacing:'.05em'}}>Titre détecté (modifiable)</label>
                        <input value={photoQuery} onChange={e=>setPhotoQuery(e.target.value)} placeholder="Corrige si besoin…"
                          style={{width:'100%',padding:'9px 10px',border:`1.5px solid ${T.border}`,borderRadius:'8px',fontFamily:F,fontSize:'13.5px',fontWeight:600,color:T.ink,background:T.surface,outline:'none'}}/>
                      </>
                    )}
                    <button type="button" onClick={()=>photoInputRef.current?.click()}
                      style={{marginTop:'8px',border:'none',background:'transparent',color:T.muted,fontSize:'12px',fontWeight:600,cursor:'pointer',padding:0,textDecoration:'underline'}}>
                      Reprendre la photo
                    </button>
                  </div>
                </div>
              )}

              {photoPreview && !ocrLoading && (
                <PrimaryBtn onClick={handlePhotoSearch} disabled={photoSearching||!photoQuery.trim()}
                  label={photoSearching
                    ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'8px'}}><Spinner size={16}/>Recherche…</span>
                    : '🔍 Rechercher cet album'}/>
              )}

              {photoError && (
                <div style={{marginTop:'12px',background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:'8px',padding:'10px 12px',fontSize:'13px',color:T.red,fontWeight:500,lineHeight:1.5}}>{photoError}</div>
              )}

              {photoResults?.length > 0 && (
                <div style={{marginTop:'14px',display:'flex',flexDirection:'column',gap:'8px'}}>
                  <div style={{fontSize:'11px',fontWeight:700,color:T.muted,textTransform:'uppercase',letterSpacing:'.05em'}}>Résultats — choisis le bon</div>
                  {photoResults.map((r,i) => (
                    <button key={i} type="button" onClick={()=>{ const { coverUrl, ...rest } = r; onFound(rest); }}
                      style={{display:'flex',gap:'10px',alignItems:'center',padding:'9px',border:`1.5px solid ${T.border}`,borderRadius:'10px',background:T.surface,cursor:'pointer',textAlign:'left',width:'100%',fontFamily:F}}
                      onMouseOver={e=>e.currentTarget.style.borderColor=T.orange}
                      onMouseOut={e=>e.currentTarget.style.borderColor=T.border}>
                      {r.coverUrl ? (
                        <img src={r.coverUrl} alt="" style={{width:'40px',height:'56px',objectFit:'cover',borderRadius:'4px',flexShrink:0}}/>
                      ) : (
                        <div style={{width:'40px',height:'56px',borderRadius:'4px',background:T.g1,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'16px'}}>📕</div>
                      )}
                      <div style={{minWidth:0,flex:1}}>
                        <div style={{fontSize:'13px',fontWeight:700,color:T.ink,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {r.serie ? `${r.serie}${r.numero?` T.${r.numero}`:''}` : (r.titre||'(Sans titre)')}
                        </div>
                        <div style={{fontSize:'11px',color:T.muted,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                          {[r.auteur||r.scenariste, r.editeur].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div style={{marginTop:'12px'}}>
                <SecondaryBtn onClick={goBack} label="← Retour"/>
              </div>
            </div>
          )}

          {/* ── Saisie manuelle ── */}
          {mode==='manual' && (
            <form onSubmit={submitManual}>
              {detected && (
                <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:'8px',padding:'9px 12px',fontSize:'13px',fontWeight:600,color:'#15803D',marginBottom:'12px'}}>
                  ✓ Code scanné : <span style={{letterSpacing:'.08em'}}>{detected}</span>
                </div>
              )}
              <div style={{marginBottom:'14px'}}>
                <label style={{display:'block',fontSize:'12px',fontWeight:600,color:T.muted,marginBottom:'6px',textTransform:'uppercase',letterSpacing:'.05em'}}>Numéro ISBN</label>
                <input type="text" value={isbn} onChange={e=>setIsbn(e.target.value.replace(/\D/g,''))}
                  placeholder="9782205041507" inputMode="numeric" maxLength={13} autoFocus
                  style={{width:'100%',padding:'14px',border:`1.5px solid ${T.border}`,borderRadius:'10px',fontFamily:F,fontSize:'22px',fontWeight:700,letterSpacing:'.12em',color:T.ink,background:T.surface,outline:'none',textAlign:'center',transition:'border-color .15s, box-shadow .15s'}}
                  onFocus={e=>{e.target.style.borderColor=T.orange;e.target.style.boxShadow='0 0 0 3px rgba(240,78,26,.12)';}}
                  onBlur={e=>{e.target.style.borderColor=T.border;e.target.style.boxShadow='none';}}/>
                <div style={{display:'flex',justifyContent:'space-between',marginTop:'5px'}}>
                  <div style={{fontSize:'11px',color:T.subtle}}>Sous le code-barres, au dos du livre</div>
                  <div style={{fontSize:'11px',fontWeight:700,color:isbn.length===13?T.green:T.subtle}}>{isbn.length}/13</div>
                </div>
              </div>
              {error && (
                <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:'8px',padding:'10px 12px',fontSize:'13px',color:T.red,marginBottom:'12px',fontWeight:500,lineHeight:1.4}}>{error}</div>
              )}
              <PrimaryBtn type="submit" disabled={loading||isbn.length<10}
                label={loading
                  ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'8px'}}><Spinner size={16}/>Recherche en cours…</span>
                  : 'Rechercher les infos'}/>
              <div style={{marginTop:'8px'}}>
                <SecondaryBtn onClick={goBack} label="← Retour"/>
              </div>
            </form>
          )}
        </div>

        <style>{`
          @keyframes slideUp  { from{opacity:0;transform:translateY(40px)} to{opacity:1;transform:translateY(0)} }
          @keyframes spin     { to{transform:rotate(360deg)} }
          @keyframes scanline { 0%,100%{top:38%} 50%{top:62%} }
        `}</style>
      </div>
    </div>
  );
}

const Spinner = ({size=24}) => (
  <div style={{width:size,height:size,border:`${size>20?3:2}px solid rgba(240,78,26,.2)`,borderTopColor:'var(--orange)',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block',flexShrink:0}}/>
);
const ChoiceBtn = ({icon,title,sub,onClick}) => (
  <button type="button" onClick={onClick}
    style={{display:'flex',alignItems:'center',gap:'14px',padding:'16px',border:'1.5px solid var(--border)',borderRadius:'12px',background:'var(--surface)',cursor:'pointer',textAlign:'left',width:'100%',transition:'all .15s',fontFamily:F}}
    onMouseOver={e=>{e.currentTarget.style.borderColor='var(--orange)';e.currentTarget.style.background='#FFF8F5';}}
    onMouseOut={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='#FFFFFF';}}>
    <div style={{width:'46px',height:'46px',borderRadius:'10px',background:'var(--orange-pale)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'22px',flexShrink:0}}>{icon}</div>
    <div style={{flex:1}}>
      <div style={{fontSize:'14px',fontWeight:700,color:'var(--ink)',marginBottom:'2px'}}>{title}</div>
      <div style={{fontSize:'12px',color:'var(--muted)'}}>{sub}</div>
    </div>
    <svg style={{flexShrink:0,color:'var(--subtle)'}} width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </button>
);
const PrimaryBtn = ({label,onClick,disabled,type='button'}) => (
  <button type={type} onClick={onClick} disabled={disabled}
    style={{width:'100%',padding:'13px',border:'none',borderRadius:'10px',background:disabled?'var(--surface2)':'var(--orange)',color:disabled?'var(--muted)':'#fff',fontFamily:F,fontSize:'15px',fontWeight:700,cursor:disabled?'not-allowed':'pointer',transition:'all .15s',display:'flex',alignItems:'center',justifyContent:'center'}}>
    {label}
  </button>
);
const SecondaryBtn = ({onClick,label}) => (
  <button type="button" onClick={onClick}
    style={{width:'100%',padding:'11px',border:'1.5px solid var(--border)',borderRadius:'10px',background:'transparent',fontFamily:F,fontSize:'14px',fontWeight:600,color:'var(--muted)',cursor:'pointer',transition:'border-color .15s'}}
    onMouseOver={e=>e.currentTarget.style.borderColor='var(--subtle)'}
    onMouseOut={e=>e.currentTarget.style.borderColor='var(--border)'}>
    {label}
  </button>
);
